import { gql } from '@apollo/client';
import IconCheck from '@apollo/icons/default/IconCheck.svg';
import IconSearch from '@apollo/icons/default/IconSearch.svg';
import {
  Avatar,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
} from '@apollo/orbit';
import { List } from '@apollo/space-kit/List';
import { ListItem } from '@apollo/space-kit/ListItem';
import classnames from 'classnames';
import { useCombobox, useMultipleSelection } from 'downshift';
import { orderBy } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { test } from 'src/components/common/typeahead/fuzzy';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

export const ReviewerMultiselectPopoverUsersServiceFragment = gql`
  fragment ReviewerMultiselectPopoverUsersServiceFragment on Service {
    id
    minProposalApprovers
    account {
      id
      memberships {
        account {
          id
        }
        user {
          id
          fullName
          avatarUrl
        }
      }
    }
  }
`;

export const ReviewerMultiselectPopover = ({
  selectedMemberIds: initialMemberIds,
  reviewedMemberIds = [],
  mandatoryDefaultReviewerMemberIds = [],
  proposalAuthorId,
  graph,
  trigger,
  onClose,
}: {
  selectedMemberIds: string[];
  reviewedMemberIds?: string[];
  mandatoryDefaultReviewerMemberIds?: string[];
  proposalAuthorId?: string;
  graph: GraphQLTypes.ReviewerMultiselectPopoverUsersServiceFragment;
  trigger: React.ReactElement;
  onClose: (selectedIds: string[]) => void;
}) => {
  const inputRef = React.createRef<HTMLInputElement>();
  const [search, setSearch] = useState('');

  const initialMembers = useMemo(
    () =>
      graph.account?.memberships
        ?.map(({ user }) => user)
        .filter((member) => initialMemberIds.includes(member.id)) ?? [],
    [graph.account?.memberships, initialMemberIds],
  );

  const [selectedMembers, setSelectedMembers] = useState(initialMembers);

  const members = useMemo(
    () =>
      orderBy(
        graph.account?.memberships
          // Do not show the proposal's author in the list of potential reviewers
          // Unless they are already a reviewer
          ?.filter(
            ({ user }) =>
              user.id !== proposalAuthorId ||
              initialMemberIds.includes(user.id),
          )
          .map(({ user }) => user),
        (user) => initialMemberIds.includes(user.id),
        ['desc'],
      ),
    [graph.account?.memberships, initialMemberIds, proposalAuthorId],
  );

  const filteredMembers = useMemo(
    () => members.filter((user) => test(search, user.fullName)),
    [members, search],
  );

  const { getDropdownProps } = useMultipleSelection({
    selectedItems: selectedMembers,
  });
  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: filteredMembers,
    itemToString: (item) => item?.id ?? '',
    initialHighlightedIndex: 0,
    selectedItem: null,
    inputValue: search,
    stateReducer: (
      { inputValue, highlightedIndex: currentHighlightedIndex },
      { changes, type },
    ) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            inputValue,
            highlightedIndex: currentHighlightedIndex,
            isOpen: true,
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue,
          };
        default:
          return changes;
      }
    },
    onStateChange: ({
      inputValue: newInputValue,
      selectedItem: newSelectedItem,
    }) => {
      if (typeof newInputValue === 'string') {
        setSearch(newInputValue);
      }
      if (newSelectedItem) {
        setSelectedMembers((currentMembers) => {
          if (
            currentMembers?.some((member) => member.id === newSelectedItem.id)
          ) {
            return currentMembers.filter(
              (member) => member.id !== newSelectedItem.id,
            );
          }
          return [...(currentMembers ?? []), newSelectedItem];
        });
      }
    },
  });

  const handleClose = useCallback(() => {
    const initialValuesAsSet = new Set(
      initialMembers.map((member) => member.id),
    );
    const selectedValuesAsSet = new Set(
      selectedMembers.map((member) => member.id),
    );
    if (
      initialValuesAsSet.size === selectedValuesAsSet.size &&
      selectedMembers.every((member) => initialValuesAsSet.has(member.id))
    ) {
      return;
    }

    onClose(selectedMembers.map((member) => member.id));
  }, [initialMembers, onClose, selectedMembers]);

  // ? Note: We should reset search on open to prevent a visual bug where the filter
  // ? clears and shows the full list before the popover is visually closed
  const handleOpen = useCallback(() => {
    setSearch('');
    setSelectedMembers(initialMembers);
  }, [initialMembers]);

  // NOTE: If a default reviewer is set after a proposal is created, they should
  // be allowed to be chosen for that proposal.
  const disabledMemberIds = useMemo(() => {
    return mandatoryDefaultReviewerMemberIds
      .filter((id) => initialMemberIds.includes(id))
      .concat(reviewedMemberIds);
  }, [mandatoryDefaultReviewerMemberIds, initialMemberIds, reviewedMemberIds]);

  return (
    <Popover
      placement="bottom-end"
      isLazy
      onOpen={handleOpen}
      onClose={handleClose}
      initialFocusRef={inputRef}
    >
      {React.cloneElement(trigger, { ...getToggleButtonProps() })}
      <PopoverContent className="w-72">
        <PopoverBody className="p-0">
          <div className="border-b border-b-silver-dark p-2">
            <Input
              placeholder="Search..."
              className="border-0"
              ref={inputRef}
              {...getInputProps({
                autoFocus: true,
                ...getDropdownProps({ preventKeyAction: isOpen }),
                onBlur: () => {},
              })}
              leftElement={<IconSearch className="pointer-events-none" />}
            />
          </div>
          {isMinLengthArray(1, filteredMembers) ? (
            <List className="max-h-72 overflow-y-auto p-2" {...getMenuProps()}>
              {isMinLengthArray(1, reviewedMemberIds) && (
                <div className="mb-2 rounded border border-orange-light bg-orange-lightest p-4 text-sm font-normal text-orange-darker">
                  Users who have already shared their reviews cannot be removed
                  from the list.
                </div>
              )}
              {filteredMembers.map((member, index) => {
                const isMandatoryDefaultReviewer =
                  disabledMemberIds.includes(member.id) &&
                  mandatoryDefaultReviewerMemberIds.includes(member.id);
                return (
                  <ListItem
                    key={member.id}
                    className="h-auto w-full px-3 py-2 [&>div]:flex [&>div]:items-center [&>div]:justify-between"
                    {...getItemProps({
                      item: member,
                      index,
                      disabled: disabledMemberIds.includes(member.id),
                    })}
                    highlighted={highlightedIndex === index}
                  >
                    <div
                      className={classnames(
                        'flex truncate',
                        disabledMemberIds.includes(member.id) &&
                          'text-secondary',
                        isMandatoryDefaultReviewer
                          ? 'items-start'
                          : 'items-center',
                      )}
                    >
                      <Avatar
                        size="sm"
                        name={member.fullName}
                        src={member.avatarUrl ?? undefined}
                        className={classnames(
                          'mr-3',
                          disabledMemberIds.includes(member.id) && 'opacity-50',
                        )}
                      />
                      <div className="flex flex-col truncate font-normal">
                        <span className="truncate text-sm">
                          {member.fullName}
                        </span>
                        {isMandatoryDefaultReviewer && (
                          <div className="text-xs text-secondary">
                            Mandatory reviewer
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedMembers.some(
                      (selectedMember) => selectedMember.id === member.id,
                    ) && (
                      <IconCheck
                        className={classnames(
                          'ml-2 text-secondary',
                          disabledMemberIds.includes(member.id)
                            ? 'opacity-40'
                            : '',
                        )}
                      />
                    )}
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <div className="p-4 text-center text-base font-normal text-secondary">
              No matching results.
            </div>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
